import { useCallback } from "react";
import type { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import {
  updateShowConfigurationBackupInstallModal,
  updateShowConfigurationInstallModal,
  updateShowFirmwareBackupInstallModal,
  updateShowFirmwareInstallModal,
  updateShowOtaDownloadModal,
  updateShowOtaPushFirmwareBackupModal,
  updateShowOtaPushFirmwareModal,
} from "@src/reducers/your-reducer";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { downloadFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/download-firmware";
import { downloadConfiguratonUseCase } from "@src/cloud-connect/ota-update/use-cases/download-configuration";
import { downloadFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/download-firmware-backup";
import { downloadConfigurationBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/download-configuration-backup";
import { postInstallFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-firmware-use-case";
import { postInstallConfigurationUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-configuration-use-case";
import { postInstallFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-firmware-backup-use-case";
import { postInstallConfigurationBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-configuration-backup-use-case";
import {
  ActiveInstallationStatus,
  BACKUPS,
  CONFIGURATIONS,
  FIRMWARE,
  FIRMWARE_TABS,
  VERSIONS,
} from "../../../cloud-connect/ota-update/constants";
import { ListItem } from "@src/types/ota-update";

type OTAType =
  | "FIRMWARE"
  | "CONFIGURATION"
  | "FIRMWAREBACKUP"
  | "CONFIGURATIONBACKUP";

interface OTAUpdateSummaryTexts {
  pushFirmwareText: string;
  pushBackupText: string;
  latestText: string;
  versionText: string;
  sizeText: string;
  digestText: string;
  uploadTimeText: string;
  uploaderAltText: string;
  checkboxText: string;
  installText: string;
  downloadText: string;
  failedMsg: string;
  errorMsg: string;
  noBackupsText: string;
  noFirmwareText: string;
  noConfigurationText: string;
  backupInfoText: string;
  firmwareInfoText: string;
  configurationInfoText: string;
  firmwareAlreadyInstalledTooltip: string;
  backupAlreadyInstalledTooltip: string;
  firmwareInstallationInProgressTooltip: string;
  backupInstallationInProgressTooltip: string;
  installationFailedCloseErrorFirmwareTooltip: string;
  installationFailedCloseErrorBackupTooltip: string;
}

export interface ReleaseField {
  label: string;
  value: string;
  isDate?: boolean;
}

interface useOTAUpdateSummaryProps {
  cardData: ListItem | null;
  onInstallStarted?: (updateId: string) => void;
  activeOTAHistoryTab: string;
  onSelectPushFirmwareRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  onSelectPushFirmwareBackupRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  activeInstallationData: ActiveInstallationProcessPayload | null;
}

interface useOTAUpdateSummaryResult {
  texts: OTAUpdateSummaryTexts;
  title: string;
  infoText: string;
  releaseFields: ReleaseField[];
  downloadPermission: string;
  onInstall: (id?: string, type?: string) => Promise<void>;
  isFirmwareAlreadyInstalled: boolean;
  isInstallationGoingOn: boolean;
  installActionTooltip: string;
  onDownload: (id?: string, type?: string) => Promise<void>;
  handlePushFirmware: () => void;
  handlePushFirmwareBackup: () => void;
  shouldShowFirmwareButton: boolean;
}

interface InstallResult {
  success: boolean;
  data?: {
    data?: {
      update_id?: string;
    };
  };
  error?: string;
}

interface DownloadResult {
  success: boolean;
  data?: BlobPart;
  filename?: string;
  handledDirectly?: boolean;
  error?: string;
}

const downloadPermissionMap: Record<OTAType, string> = {
  FIRMWARE: "manage-firmware",
  CONFIGURATION: "manage-configuration",
  FIRMWAREBACKUP: "manage-firmware-backup",
  CONFIGURATIONBACKUP: "manage-configuration-backup",
};

const modalActionMap: Record<OTAType, (show: boolean) => AnyAction> = {
  FIRMWARE: updateShowFirmwareInstallModal,
  FIRMWAREBACKUP: updateShowFirmwareBackupInstallModal,
  CONFIGURATION: updateShowConfigurationInstallModal,
  CONFIGURATIONBACKUP: updateShowConfigurationBackupInstallModal,
};

const installMap: Record<
  OTAType,
  (body: Record<string, unknown>) => Promise<InstallResult>
> = {
  FIRMWARE: postInstallFirmwareUseCase,
  CONFIGURATION: postInstallConfigurationUseCase,
  FIRMWAREBACKUP: postInstallFirmwareBackupUseCase,
  CONFIGURATIONBACKUP: postInstallConfigurationBackupUseCase,
};

const downloadMap: Record<
  OTAType,
  (id: string, type: string) => Promise<DownloadResult>
> = {
  FIRMWARE: downloadFirmwareUseCase,
  CONFIGURATION: downloadConfiguratonUseCase,
  FIRMWAREBACKUP: downloadFirmwareBackupUseCase,
  CONFIGURATIONBACKUP: downloadConfigurationBackupUseCase,
};

const isOTAType = (value: string): value is OTAType =>
  [
    "FIRMWARE",
    "CONFIGURATION",
    "FIRMWAREBACKUP",
    "CONFIGURATIONBACKUP",
  ].includes(value);

export const useOTAUpdateSummary = ({
  cardData,
  onInstallStarted,
  activeOTAHistoryTab,
  onSelectPushFirmwareRow,
  onSelectPushFirmwareBackupRow,
  activeInstallationData,
}: useOTAUpdateSummaryProps): useOTAUpdateSummaryResult => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );

  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext?.userId ?? "",
  );

  const otaActiveView = useSelector(
    (state: RootState) => state.yourSlice.otaActiveView,
  );

  const texts = {
    latestText: i18n("modules.otaUpdateSummary.latestText"),
    versionText: i18n("modules.otaUpdateSummary.versionText"),
    sizeText: i18n("modules.otaUpdateSummary.sizeText"),
    digestText: i18n("modules.otaUpdateSummary.digestText"),
    uploadTimeText: i18n("modules.otaUpdateSummary.uploadTimeText"),
    uploaderAltText: i18n("modules.otaUpdateSummary.uploaderAltText"),
    checkboxText: i18n("modules.otaUpdateSummary.checkboxText"),
    installText: i18n("modules.otaUpdateSummary.installText"),
    downloadText: i18n("modules.otaUpdateSummary.downloadText"),
    failedMsg: i18n("modules.otaUpdateSummary.failedMsg"),
    errorMsg: i18n("modules.otaUpdateSummary.errorMsg"),
    noBackupsText: i18n("modules.otaUpdateSummary.noBackupsText"),
    noFirmwareText: i18n("modules.otaUpdateSummary.noFirmwareText"),
    noConfigurationText: i18n("modules.otaUpdateSummary.noConfigurationText"),
    backupInfoText: i18n("modules.otaUpdateSummary.backupInfoText"),
    firmwareInfoText: i18n("modules.otaUpdateSummary.firmwareInfoText"),
    configurationInfoText: i18n(
      "modules.otaUpdateSummary.configurationInfoText",
    ),
    pushFirmwareText: i18n("modules.otaUpdateSummary.pushFirmwareText"),
    pushBackupText: i18n("modules.otaUpdateSummary.pushBackupText"),
    firmwareAlreadyInstalledTooltip: i18n(
      "modules.otaUpdateSummary.firmwareAlreadyInstalledTooltip",
    ),
    backupAlreadyInstalledTooltip: i18n(
      "modules.otaUpdateSummary.backupAlreadyInstalledTooltip",
    ),
    firmwareInstallationInProgressTooltip: i18n(
      "modules.otaUpdateSummary.firmwareInstallationInProgressTooltip",
    ),
    backupInstallationInProgressTooltip: i18n(
      "modules.otaUpdateSummary.backupInstallationInProgressTooltip",
    ),
    installationFailedCloseErrorFirmwareTooltip: i18n(
      "modules.otaUpdateSummary.installationFailedCloseErrorFirmwareTooltip",
    ),
    installationFailedCloseErrorBackupTooltip: i18n(
      "modules.otaUpdateSummary.installationFailedCloseErrorBackupTooltip",
    ),
  };

  const downloadPermission =
    cardData?.type && isOTAType(cardData.type)
      ? downloadPermissionMap[cardData.type]
      : "";

  const emptyTitleMap: Record<string, string> = {
    [BACKUPS]: texts.noBackupsText,
    [VERSIONS]: texts.noFirmwareText,
    [CONFIGURATIONS]: texts.noConfigurationText,
  };

  const title = cardData?.filename || emptyTitleMap[activeOTAHistoryTab] || "";

  const infoMap: Record<string, string> = {
    [BACKUPS]: texts.backupInfoText,
    [VERSIONS]: texts.firmwareInfoText,
    [CONFIGURATIONS]: texts.configurationInfoText,
  };

  const infoText = infoMap[activeOTAHistoryTab] || "";

  const releaseFields = !cardData
    ? []
    : [
        { label: texts.digestText, value: cardData.digest ?? "" },
        { label: texts.sizeText, value: formatBytes(cardData.size_bytes) },
        {
          label: texts.uploadTimeText,
          value: cardData.uploaded_at ?? "",
          isDate: true,
        },
      ];

  const isFirmwareAlreadyInstalled =
    cardData?.version === connectionView?.source?.current_firmware_version;

  const isInstallationGoingOn = activeInstallationData?.update_id != null;
  const isInstallationFailed =
    activeInstallationData?.status === ActiveInstallationStatus.FAILED;

  const versionForTooltip =
    activeInstallationData?.firmware?.version || cardData?.version || "";

  const shouldShowFirmwareButton =
    otaActiveView === FIRMWARE &&
    activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS;

  let installActionTooltip = "";

  if (isInstallationFailed) {
    installActionTooltip =
      activeOTAHistoryTab === BACKUPS
        ? texts.installationFailedCloseErrorBackupTooltip
        : texts.installationFailedCloseErrorFirmwareTooltip;
  } else if (isInstallationGoingOn) {
    installActionTooltip = (
      activeOTAHistoryTab === BACKUPS
        ? texts.backupInstallationInProgressTooltip
        : texts.firmwareInstallationInProgressTooltip
    ).replace("{version}", versionForTooltip);
  } else if (isFirmwareAlreadyInstalled) {
    installActionTooltip = (
      activeOTAHistoryTab === BACKUPS
        ? texts.backupAlreadyInstalledTooltip
        : texts.firmwareAlreadyInstalledTooltip
    ).replace("{version}", versionForTooltip);
  }

  const onInstall = useCallback(
    async (id?: string, type?: string) => {
      if (!id || !type || !isOTAType(type)) {
        errorSnackbar("Invalid OTA type for installation", 5000);
        return;
      }

      try {
        const body = {
          firmware_id: id,
          uploader_id: userId,
          install_at: new Date().toISOString(),
          object_type: type,
        };

        const result = await installMap[type](body);

        if (result.success) {
          const updateId = result.data?.data?.update_id;

          if (!updateId) {
            errorSnackbar("Failed to retrieve update id", 5000);
            return;
          }

          onInstallStarted?.(updateId);
          dispatch(modalActionMap[type](true));
        } else {
          dispatch(modalActionMap[type](false));
          errorSnackbar(
            texts.failedMsg || result.error || texts.errorMsg,
            5000,
          );
        }
      } catch (error) {
        errorSnackbar(texts.errorMsg, 5000);
      }
    },
    [dispatch, onInstallStarted, texts.errorMsg, texts.failedMsg, userId],
  );

  const onDownload = useCallback(
    async (id?: string, type?: string) => {
      if (!id || !type || !isOTAType(type)) {
        errorSnackbar(texts.errorMsg, 5000);
        return;
      }

      dispatch(updateShowOtaDownloadModal(true));

      const result = await downloadMap[type](id, type);

      if (result.success) {
        if (result.handledDirectly) {
          return;
        }

        if (result.data == null || !result.filename) {
          errorSnackbar(texts.errorMsg, 5000);
          return;
        }

        const blob = new Blob([result.data]);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = result.filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } else {
        errorSnackbar(result.error || texts.errorMsg, 5000);
      }
    },
    [dispatch, texts.errorMsg],
  );

  const handlePushFirmware = () => {
    onSelectPushFirmwareRow?.(cardData);
    dispatch(updateShowOtaPushFirmwareModal(true));
  };

  const handlePushFirmwareBackup = () => {
    onSelectPushFirmwareBackupRow?.(cardData);
    dispatch(updateShowOtaPushFirmwareBackupModal(true));
  };

  return {
    texts,
    title,
    infoText,
    releaseFields,
    downloadPermission,
    onInstall,
    isFirmwareAlreadyInstalled,
    isInstallationGoingOn,
    installActionTooltip,
    onDownload,
    handlePushFirmware,
    handlePushFirmwareBackup,
    shouldShowFirmwareButton,
  };
};
